import { CognitoUser } from "utils/cognito-auth";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { usePostHog } from "@posthog/react";
import { Auth } from "utils/cognito-auth";
import {
  User,
  useIdentity,
  useIdentityContext,
  userCreateOrganisation,
} from "contexts/identity-context";
import * as QueryKeys from "data";
import { fetchUserMemberships } from "data/queries-common";
import GeoscapeDeveloperSvg from "icons/hub-logo-signIn.svg?react";
import { cn } from "@/lib/utils";
import { CheckCircle, ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router";
import { useTheme } from "@/components/theme-provider";
import { TruncatedText } from "components/shared/truncated-text";
import { MfaSetupOnboarding } from "./mfa-setup-onboarding";
import { INDUSTRIES, services, workDescription } from "./states";
import { UserInvitationsOnBoarding } from "./user-invitations-onboarding";

export interface CreateOrganisationBody {
  workDescription: string;
  selectedServices: string[];
  industry: string;
  name: string;
}

export const setCompletedWizardFlag = async (user: CognitoUser) => {
  await Auth.updateUserAttributes(user, {
    "custom:onboardingComplete": String(true),
  });
};

const OnboardingWizard = () => {
  const [identityState] = useIdentity();
  const { refreshIdentity } = useIdentityContext();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const posthog = usePostHog();
  const user = identityState as User;

  const [activeStep, setActiveStep] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasHandledInvitations, setHasHandledInvitations] = useState(false);

  const membershipsQuery = useQuery({
    queryKey: [QueryKeys.userMembership, user.cognitoSub],
    queryFn: () => fetchUserMemberships(true),
  });

  // If the user accepted an invite in a previous session and returns mid-wizard,
  // there will be memberships but no pending ones. Mark invitations handled so
  // they can proceed past step 2 without being stuck.
  useEffect(() => {
    if (!membershipsQuery.isSuccess) return;
    const hasPending = membershipsQuery.data.some((m) => m.status === "Pending");
    if (!hasPending && membershipsQuery.data.length > 0) {
      setHasHandledInvitations(true);
    }
  }, [membershipsQuery.isSuccess]);

  const [formData, setFormData] = useState<{
    workDescription: string;
    selectedServices: string[];
  }>({
    workDescription: "",
    selectedServices: [],
  });

  const [formData2, setFormData2] = useState({
    name: "",
    industry: "",
  });

  const [isMfaSetup, setisMfaSetup] = useState(false);
  const [startFromStep, setStartFromStep] = useState(1);

  const steps = ["Profile", "Organization", "MFA"];

  const validateStep = (showErrors: boolean = true) => {
    const newErrors: Record<string, string> = {};

    switch (activeStep) {
      case 0:
        if (!formData.workDescription.trim()) {
          newErrors.workDescription = "Work description is required";
        }
        if (formData.selectedServices.length === 0) {
          newErrors.selectedServices = "Please select at least one service";
        }
        break;
      case 1:
        if (!membershipsQuery.isSuccess) {
          newErrors.loading = "Loading...";
          break;
        }
        if (membershipsQuery.data.length === 0) {
          if (!formData2.name.trim()) {
            newErrors.name = "Organization name is required";
          }
          if (!formData2.industry) {
            newErrors.industry = "Industry is required";
          }
          break;
        }
        if (hasHandledInvitations == false) {
          newErrors.invitations =
            "Ensure that all invitations are processed before proceeding with the next steps.";
          break;
        }
        break;
    }
    if (showErrors) {
      setErrors(newErrors);
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validateStep()) return;
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const toggleService = (service: string) => {
    setFormData((prev) => ({
      ...prev,
      selectedServices: prev.selectedServices.includes(service)
        ? prev.selectedServices.filter((s) => s !== service)
        : [...prev.selectedServices, service],
    }));
    if (errors.selectedServices) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.selectedServices;
        return newErrors;
      });
    }
  };

  const handleCreateOrganisation = async (name: string) => {
    setIsLoading(true);
    try {
      const newOrg = await userCreateOrganisation({
        ...formData,
        ...formData2,
      });
      return newOrg;
    } catch (error) {
      console.error("Error creating organisation", error);
      setErrors((prev) => ({
        ...prev,
        organisation: "Failed to create organization. Please try again.",
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const completeWizard = async () => {
    if (user.organisations.length === 0) {
      const newOrg = await handleCreateOrganisation(formData2.name);
      if (!newOrg) return;
    }
    await setCompletedWizardFlag(user.cognitoUser);
    await refreshIdentity();
  };

  const submitWorkDescription = async (wd: string) => {
    setIsLoading(true);
    await Auth.updateUserAttributes(user.cognitoUser, {
      "custom:role": wd,
    }).finally(() => {
      setIsLoading(false);
    });
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <div className="flex flex-col gap-4 w-[346px]">
            <p className="text-sm">Tell us a bit about yourself</p>

            <div className="space-y-1.5">
              <Label>What best describes your work?</Label>
              <Select
                value={formData.workDescription}
                onValueChange={(value) => {
                  setFormData((prev) => ({ ...prev, workDescription: value }));
                  if (errors.workDescription) {
                    setErrors((prev) => {
                      const e = { ...prev };
                      delete e.workDescription;
                      return e;
                    });
                  }
                }}
                disabled={!membershipsQuery.isSuccess}
              >
                <SelectTrigger
                  className={errors.workDescription ? "border-destructive" : ""}
                >
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {workDescription
                    .sort((a, b) => a.localeCompare(b))
                    .map((item) => (
                      <SelectItem key={item} value={item}>
                        {item}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {errors.workDescription && (
                <p className="text-xs text-destructive">
                  {errors.workDescription}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>What Hub services are you most interested in?</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    disabled={!membershipsQuery.isSuccess}
                    className={cn(
                      "border-input data-[placeholder]:text-muted-foreground bg-input/20 dark:bg-input/30 dark:hover:bg-input/50 focus-visible:border-ring focus-visible:ring-ring/30 gap-1.5 rounded-md border px-2 py-1.5 text-xs/relaxed transition-colors focus-visible:ring-[2px] h-7 flex w-full items-center justify-between whitespace-nowrap outline-none disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
                      errors.selectedServices && "border-destructive",
                    )}
                  >
                    <TruncatedText
                      as="span"
                      className="min-w-0 text-left"
                      tooltip={formData.selectedServices
                        .slice()
                        .sort((a, b) => a.localeCompare(b))
                        .join(", ")}
                    >
                      {formData.selectedServices.length > 0 ? (
                        formData.selectedServices
                          .slice()
                          .sort((a, b) => a.localeCompare(b))
                          .join(", ")
                      ) : (
                        <span className="text-muted-foreground">
                          Select services...
                        </span>
                      )}
                    </TruncatedText>
                    <ChevronDown className="text-muted-foreground size-3.5 shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="w-[var(--radix-popover-trigger-width)] p-2"
                >
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {services
                      .sort((a, b) => a.localeCompare(b))
                      .map((service) => (
                        <div key={service} className="flex items-center gap-2">
                          <Checkbox
                            id={`service-${service}`}
                            checked={formData.selectedServices.includes(
                              service,
                            )}
                            onCheckedChange={() => toggleService(service)}
                          />
                          <Label
                            htmlFor={`service-${service}`}
                            className="text-sm font-normal cursor-pointer"
                          >
                            {service}
                          </Label>
                        </div>
                      ))}
                  </div>
                </PopoverContent>
              </Popover>
              {errors.selectedServices && (
                <p className="text-xs text-destructive">
                  {errors.selectedServices}
                </p>
              )}
            </div>
          </div>
        );
      case 1:
        return (
          <div className="flex flex-col gap-4 w-[346px]">
            {!membershipsQuery.isSuccess ? (
              <></>
            ) : user.organisations.length !== 0 &&
              hasHandledInvitations == true ? (
              <Alert>
                <AlertDescription>All invitations processed</AlertDescription>
              </Alert>
            ) : membershipsQuery.data.length !== 0 ? (
              <>
                <UserInvitationsOnBoarding
                  userState={user}
                  setHasHandledInvitations={setHasHandledInvitations}
                />
                {errors.invitations && (
                  <p className="text-xs text-destructive">
                    {errors.invitations}
                  </p>
                )}
              </>
            ) : (
              <>
                <p className="text-sm">Tell us about your organisation</p>
                <div className="space-y-1.5">
                  <Label htmlFor="organisation-name">Organisation Name</Label>
                  <Input
                    id="organisation-name"
                    value={formData2.name}
                    onChange={(e) => {
                      setFormData2((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }));
                      if (errors.name) {
                        setErrors((prev) => {
                          const e = { ...prev };
                          delete e.name;
                          return e;
                        });
                      }
                    }}
                    maxLength={120}
                    disabled={isLoading}
                    className={errors.name ? "border-destructive" : ""}
                  />
                  {errors.name && (
                    <p className="text-xs text-destructive">{errors.name}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>Industry</Label>
                  <Select
                    value={formData2.industry}
                    onValueChange={(value) => {
                      setFormData2((prev) => ({ ...prev, industry: value }));
                      if (errors.industry) {
                        setErrors((prev) => {
                          const e = { ...prev };
                          delete e.industry;
                          return e;
                        });
                      }
                    }}
                    disabled={isLoading}
                  >
                    <SelectTrigger
                      className={errors.industry ? "border-destructive" : ""}
                    >
                      <SelectValue placeholder="Select industry..." />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map((item) => (
                        <SelectItem key={item} value={item}>
                          {item}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.industry && (
                    <p className="text-xs text-destructive">
                      {errors.industry}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        );
      case 2:
        return (
          <>
            <p className="text-sm text-muted-foreground w-[346px]">
              Geoscape recommends you protect your account with MFA (Multi
              Factor Authentication).
            </p>
            <Alert>
              <AlertDescription>
                MFA is optional but recommended for security.
              </AlertDescription>
            </Alert>
          </>
        );
      default:
        return <p>Step {activeStep + 1} content</p>;
    }
  };

  return (
    <div className="w-full h-screen relative">
      <div className="flex flex-col items-center h-full pt-16 bg-background overflow-auto">
        <div className="flex justify-center">
          {useLocation().pathname !== "/sign-up" && (
            <GeoscapeDeveloperSvg
              fill={isDark ? "white" : "black"}
              style={{ width: "450px", height: "100px", padding: "0px" }}
            />
          )}
        </div>
        <div className="h-8" />
        <div className="flex flex-col md:flex-row gap-8">
          {errors.organisation ? (
            <Alert variant="destructive">
              <AlertDescription>
                Failed to create organisation. Contact support or try again
                later
              </AlertDescription>
            </Alert>
          ) : isMfaSetup ? (
            <MfaSetupOnboarding
              startFromStep={startFromStep}
              onSubmit={completeWizard}
              onOptOut={completeWizard}
              cognitoUser={user.cognitoUser}
            />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Onboarding Wizard</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                {/* Stepper */}
                <div className="flex w-[346px] justify-between">
                  {steps.map((label, index) => (
                    <div key={label} className="flex items-center gap-1.5">
                      <div
                        className={`flex items-center justify-center size-6 rounded-full text-xs font-medium shrink-0 ${
                          index <= activeStep
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {index < activeStep ? (
                          <CheckCircle className="size-4" />
                        ) : (
                          index + 1
                        )}
                      </div>
                      <span
                        className={`text-xs ${index <= activeStep ? "font-medium" : "text-muted-foreground"}`}
                      >
                        {label}
                      </span>
                    </div>
                  ))}
                </div>

                {!membershipsQuery.isSuccess ? (
                  <div className="flex justify-center w-full py-4">
                    <Spinner />
                  </div>
                ) : (
                  renderStepContent()
                )}

                <div className="flex justify-between w-full">
                  <Button
                    variant="outline"
                    disabled={
                      activeStep === 0 ||
                      isLoading ||
                      !membershipsQuery.isSuccess
                    }
                    onClick={handleBack}
                  >
                    Back
                  </Button>
                  {activeStep !== steps.length - 1 ? (
                    <Button
                      onClick={handleNext}
                      disabled={
                        isLoading ||
                        !membershipsQuery.isSuccess ||
                        !validateStep(false)
                      }
                    >
                      {isLoading ? <Spinner /> : "Continue"}
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        id="optOutMfa"
                        variant="outline"
                        disabled={isLoading}
                        onClick={async () => {
                          setStartFromStep(-1);
                          setisMfaSetup(true);
                          submitWorkDescription(formData.workDescription);
                          posthog.capture("user_hub_onboarding", {
                            workDescription: formData.workDescription,
                            selectedServices: formData.selectedServices,
                            firstLoginAt: new Date().toISOString(),
                          });
                        }}
                      >
                        {isLoading ? <Spinner /> : "OPT OUT"}
                      </Button>
                      <Button
                        disabled={isLoading}
                        onClick={async () => {
                          setStartFromStep(1);
                          setisMfaSetup(true);
                          submitWorkDescription(formData.workDescription);
                          posthog.capture("user_hub_onboarding", {
                            workDescription: formData.workDescription,
                            selectedServices: formData.selectedServices,
                            firstLoginAt: new Date().toISOString(),
                          });
                        }}
                      >
                        {isLoading ? <Spinner /> : "Setup MFA"}
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
